import _ from "lodash";

/**
 * Created by Eran.Shain on 11/2/2016.
 */
import { icons } from "@similarweb/icons";

import { chosenItems } from "app/common/services/chosenItems";
import { watermarkService } from "app/common/services/watermarkService";
import { CHART_COLORS } from "app/constants/ChartColors";
import { CHART_ICONS } from "app/constants/ChartIcons";
import { i18nFilter } from "app/filters/i18nFilter";

import { Bar } from "./Bar";
export class DemographicsBar extends Bar {
    isCompare: boolean;
    barWidth: number;
    barShowLegend: boolean;
    hideCategoriesIcons = true;
    categoryColors: string[];
    categoryIcons: string[];
    forceSetupColors: boolean;
    widgetColorsFrom: string;
    numComparedItems: number;
    seriesItemsMeta: any[];

    // passing seriesItemsMeta into constructor for instances where chosenItems is empty, for example in dashboards.
    constructor(options: any, data: any, seriesItemsMeta?: any) {
        super(options, data);
        this.numComparedItems = options.numComparedItems;
        this.isCompare = options.isCompare;
        this.barWidth = options.viewOptions.barWidth;
        this.barShowLegend = options.viewOptions.barShowLegend;
        this.hideCategoriesIcons = this.options.viewOptions.hideCategoriesIcons;
        this.forceSetupColors = options.viewOptions.forceSetupColors;
        this.widgetColorsFrom = options.viewOptions.widgetColorsFrom;
        this.categoryColors = options.viewOptions.categoryColors
            ? CHART_COLORS[options.viewOptions.categoryColors]
            : null;
        this.categoryIcons = options.viewOptions.categoryIcons ? CHART_ICONS[options.viewOptions.categoryIcons] : null;

        if (this.forceSetupColors && this.widgetColorsFrom) {
            this.colors = CHART_COLORS[this.widgetColorsFrom].slice();
        }
        this.seriesItemsMeta = seriesItemsMeta;
    }

    private getAppName(appCode) {
        let seriesItemMetaTitle;
        let item = chosenItems.$all().find((item) => item.Id === appCode);
        if (!item && !!this.seriesItemsMeta) {
            item = this.seriesItemsMeta.find((item) => item.id === appCode);
            seriesItemMetaTitle = item?.name;
        }
        const { Title = appCode } = item || ({} as any);
        return seriesItemMetaTitle ?? Title;
    }

    private getAppColor(appCode) {
        let seriesItemMetaColor;
        let item = chosenItems.$all().find((item) => item.Id === appCode);
        if (!item && !!this.seriesItemsMeta) {
            item = this.seriesItemsMeta.find((item) => item.id === appCode);
            seriesItemMetaColor = item?.color;
        }
        const { paletteColor = appCode } = item || ({} as any);
        return seriesItemMetaColor && paletteColor;
    }

    public getCategories() {
        const i18n: any = i18nFilter();
        const { hideCategoriesIcons } = this;
        const { categoryIcons } = this;
        const nonUniqueCategories = Object.keys(this.data).reduce((allCategories, appCode) => {
            const appCategories = Object.keys(this.data[appCode]);
            return [...allCategories, ...appCategories];
        }, []);
        const categories = _.uniq(nonUniqueCategories);

        const categoryHTML = categories.map((item, index) => {
            const icon = categoryIcons ? categoryIcons[index] : null;
            return hideCategoriesIcons || !categoryIcons
                ? `<div class="barLabel u-flex-row u-justifyCenter"> ${i18n(item)} </div>`
                : `<div class="barLabel u-flex-row u-justifyCenter">
                              <span class="bar-icon">${icons[icon]}</span>
                              <span>${i18n(item)}</span>
                        </div>`;
        });

        return categoryHTML;
    }

    getSeries(): any {
        const colors = [...this.colors];
        if (!this.isCompare) {
            const appCode = Object.keys(this.data)[0];
            return [
                {
                    name: this.getAppName(appCode),
                    data: _.map(_.head(_.values(this.data)), (val, key) => ({
                        key: key,
                        y: val,
                        color: colors.shift(),
                    })),
                },
            ];
        } else {
            return _.map(this.data, (agesObject, appCode) => {
                return {
                    name: this.getAppName(appCode),
                    data: _.values(agesObject),
                    color: this.forceSetupColors ? colors.shift() : this.getAppColor(appCode),
                };
            });
        }
    }

    getColors() {
        if (this.forceSetupColors && this.widgetColorsFrom) {
            return CHART_COLORS[this.widgetColorsFrom].slice();
        }
        return CHART_COLORS.compareMainColors.slice();
    }

    getGroupPadding(numOfItems) {
        return 0.45 - numOfItems * 0.05;
    }

    getChartConfig() {
        const { barWidth } = this;
        const { barShowLegend } = this;
        const { isCompare } = this;
        return _.merge(super.getChartConfig(), {
            options: {
                chart: {
                    height: this.options.height || 240,
                    margin: [20, 0, 30, 35],
                    events: {
                        load: function () {
                            const chart = this;
                            watermarkService.add.call(chart, { opacity: 0.1 });
                        },
                        resize: function () {
                            const chart = this;
                            setTimeout(function () {
                                //fix for chart width
                                try {
                                    chart.reflow();
                                } catch (e) {}
                            }, 1000);
                        },
                    },
                },
                yAxis: {
                    max: null,
                    endOnTick: true,
                    labels: {
                        style: {
                            color: "#A3A3A3",
                            fontSize: "11px",
                        },
                    },
                },
                xAxis: {
                    labels: {
                        useHTML: true,
                        style: {
                            fontSize: "22px",
                            lineHeight: "22px",
                        },
                    },
                },
                credits: {
                    enabled: false,
                },
                plotOptions: {
                    column: {
                        borderWidth: 0,
                        groupPadding: this.getGroupPadding(this.numComparedItems),
                        pointWidth: isCompare ? null : barWidth,
                        maxPointWidth: barWidth,
                        pointPadding: 0.1,
                        grouping: true,
                        dataLabels: {
                            useHTML: false,
                            enabled: barShowLegend,
                            style: {
                                color: "#707070",
                                fontSize: "12px",
                                fontWeight: 400,
                            },
                            zIndex: 0,
                        },
                    },
                },
                tooltip: {
                    useHTML: true,
                    borderWidth: 0,
                    backgroundColor: "#fff",
                    headerFormat: "<span></span>",
                    style: {
                        fontSize: "14px",
                        fontWeight: 300,
                    },
                    pointFormatter: function () {
                        const y = (Math.round(this.y * 100) / 100).toFixed(2);
                        return `<span class="barTooltip-name">
                                            <span style="margin-right:5px; color:${this.color};">\u25CF</span>
                                            <span style="margin-right:5px; font-weight: ${
                                                this.isHover && this.series.isHover ? "bold" : "normal"
                                            }">${this.series.name}</span>
                                                <b style="color:${this.color};font-weight: bold;">${y}%</b>
                                                <br/>
                                        </span>`;
                    },
                },
            },
        });
    }
}
